<template>
  <div class="d-flex justify-center">
    <div class="pa-4 mt-4" style="max-width:600px; width:100%;">
      <h2 class="headline mb-4 text-center">Crear cuenta</h2>

      <v-card class="pa-4">
        <v-form v-model="valid" ref="form">
          <!-- Identificación -->
          <div class="subtitle-1 font-weight-medium mb-2">Identificación</div>
          <v-text-field
            v-model="user.rut"
            label="Rut"
            outlined
            :rules="[req, () => validRut || 'Rut inválido']"
          ></v-text-field>

          <!-- Datos personales -->
          <div class="subtitle-1 font-weight-medium mb-2 mt-2">Datos personales</div>
          <v-text-field v-model="user.name" label="Nombres" outlined :rules="[req]"></v-text-field>
          <v-text-field v-model="user.fLastName" label="Apellido paterno" outlined :rules="[req]"></v-text-field>
          <v-text-field v-model="user.mLastName" label="Apellido materno (opcional)" outlined></v-text-field>
          <v-menu
            v-model="birthMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="user.birthDate"
                label="Fecha de nacimiento"
                prepend-inner-icon="mdi-calendar"
                readonly
                outlined
                v-bind="attrs"
                v-on="on"
                :rules="[req]"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="user.birthDate"
              no-title
              :max="today"
              :active-picker.sync="activePicker"
              @input="birthMenu = false"
            ></v-date-picker>
          </v-menu>
          <v-select
            v-model="user.gender"
            :items="genderOptions"
            label="Género"
            outlined
            :rules="[req]"
          ></v-select>

          <!-- Contacto -->
          <div class="subtitle-1 font-weight-medium mb-2 mt-2">Datos de contacto</div>
          <v-text-field
            v-model="user.phoneNumber"
            label="Teléfono celular"
            outlined
            prefix="+56 9"
            hint="8 dígitos"
            persistent-hint
            :rules="[req, phoneRule]"
          ></v-text-field>
          <v-text-field
            class="mt-2"
            v-model="user.email"
            label="Email"
            outlined
            type="email"
            :rules="[req, emailRule]"
          ></v-text-field>

          <!-- Previsión -->
          <div class="subtitle-1 font-weight-medium mb-2 mt-2">Previsión</div>
          <v-select
            v-model="user.agreementType"
            :items="[
              { text: 'Fonasa', value: 'fonasa' },
              { text: 'Particular / Isapre', value: 'particular' }
            ]"
            label="Previsión"
            outlined
            :rules="[req]"
          ></v-select>

          <!-- Contraseña x2 -->
          <div class="subtitle-1 font-weight-medium mb-2 mt-2">Contraseña</div>
          <v-text-field
            v-model="user.pass"
            label="Contraseña"
            outlined
            type="password"
            :rules="[req, () => (user.pass || '').length >= 6 || 'Mínimo 6 caracteres']"
          ></v-text-field>
          <v-text-field
            v-model="passConfirm"
            label="Repetir contraseña"
            outlined
            type="password"
            :rules="[req, () => passConfirm === user.pass || 'Las contraseñas no coinciden']"
          ></v-text-field>

          <!-- Términos -->
          <v-checkbox v-model="user.terms" :rules="[v => !!v || 'Debe aceptar los términos']">
            <template v-slot:label>
              <div>
                Acepto los
                <a href="javascript:;" @click.stop="showTerms = true">términos y condiciones</a>
              </div>
            </template>
          </v-checkbox>
        </v-form>

        <v-btn color="primary" block large :loading="registering" :disabled="!valid" @click="register">
          Crear cuenta
        </v-btn>
        <v-btn to="/ingreso" text block class="mt-2">Ya tengo cuenta</v-btn>
      </v-card>
    </div>

    <!-- Popup de compromiso -->
    <v-dialog v-model="showTerms" max-width="520">
      <v-card class="pa-5">
        <h3 class="title mb-3">Compromiso de asistencia</h3>
        <p class="body-2">
          Días antes de tu consulta, te contactaremos por email, teléfono y/o WhatsApp
          para que confirmes o anules tu hora. Al momento de confirmar tu hora, te
          comprometes a venir a la clínica el día y la hora que reservaste.
        </p>
        <p class="body-2">
          Si no puedes venir, es muy importante que anules, porque así le das la
          oportunidad a otra persona de atenderse.
        </p>
        <div class="text-right">
          <v-btn color="primary" text @click="acceptTerms">Entendido</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showError" :timeout="6000" color="error" top>
      {{ errorMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import { isValidRut, formatRut, toApiFormat } from "../utils/rut";

export default {
  name: "Register",
  data: () => ({
    valid: false,
    registering: false,
    birthMenu: false,
    activePicker: null,
    showTerms: false,
    showError: false,
    errorMessage: "",
    passConfirm: "",
    user: {},
    genderOptions: [
      { text: "Femenino", value: "femenino" },
      { text: "Masculino", value: "masculino" },
      { text: "No binario", value: "no_binario" },
      { text: "Otro", value: "otro" },
      { text: "Prefiero no especificar", value: "no_especificado" }
    ]
  }),
  computed: {
    validRut() {
      return isValidRut(this.user.rut);
    },
    today() {
      return new Date().toISOString().substr(0, 10);
    }
  },
  watch: {
    birthMenu(val) {
      val && setTimeout(() => (this.activePicker = "YEAR"));
    }
  },
  methods: {
    req(v) {
      return !!v || "Campo requerido";
    },
    phoneRule(v) {
      const c = (v || "").toString().replace(/\D/g, "");
      return c.length === 8 || "Debe tener 8 dígitos (+56 9 XXXXXXXX)";
    },
    emailRule(v) {
      return /.+@.+\..+/.test(v || "") || "Email inválido";
    },
    acceptTerms() {
      this.user = { ...this.user, terms: true };
      this.showTerms = false;
    },
    async register() {
      if (!this.$refs.form.validate()) return;
      this.registering = true;
      try {
        await this.$store.dispatch("register", {
          rut: toApiFormat(this.user.rut),
          name: this.user.name,
          fLastName: this.user.fLastName,
          mLastName: this.user.mLastName,
          birthDate: this.user.birthDate,
          gender: this.user.gender,
          agreementType: this.user.agreementType,
          email: this.user.email,
          phoneNumber: "9" + (this.user.phoneNumber || "").toString().replace(/\D/g, ""),
          pass: this.user.pass,
          termsAccepted: true
        });
        if (this.$store.getters.getFromUrl) {
          this.$router.push(this.$store.getters.getFromUrl);
          this.$store.dispatch("saveFromUrl", "");
        } else {
          this.$router.push("/");
        }
      } catch (err) {
        this.errorMessage =
          (err.response && err.response.data && err.response.data.msg) ||
          "Error al crear la cuenta. Intente nuevamente.";
        this.showError = true;
      } finally {
        this.registering = false;
      }
    }
  },
  mounted() {
    if (this.$route.query.rut) {
      this.user = { ...this.user, rut: formatRut(this.$route.query.rut) };
    }
  }
};
</script>

<style scoped>
</style>
